import { ITechnicianAdapter, TechnicianJobFilter } from './ITechnicianAdapter';
import { ApiResponse } from '../../types/api';
import {
  TechnicianJob,
  JobPhoto,
  MaterialLog,
  ProgressUpdate,
  JobStatus,
  AcceptJobDto,
  StartJobDto,
  AddPhotoDto,
  AddMaterialDto,
  AddProgressDto,
  CompleteJobDto,
} from '../../types/technicianJob';
import { ApiClient } from '../apiClient';
import { hasApiEndpoint } from '../../config/backend';

const DEMO_JOBS: TechnicianJob[] = [
  {
    job_id: 'WO-2026-00001',
    work_order_id: 'WO-2026-00001',
    complaint_id: 'CMP-2026-00001',
    title: 'Split AC Not Cooling — Block A Server Room',
    description: 'Split AC unit in server room producing warm air. Cooling efficiency dropped significantly since last week. Immediate attention required to prevent equipment overheating.',
    location: 'Plant 1 / Block A / Floor 2 / Server Room',
    category: 'HVAC & AC Systems',
    priority: 'high',
    status: 'assigned',
    assigned_at: '2026-09-10T08:30:00Z',
    sla_deadline: '2026-09-10T16:30:00Z',
    technician_id: 'USR-00006',
    technician_name: 'Ahmad Khan',
    photos: [],
    materials: [],
    progress_updates: [
      { id: 'PU-001', message: 'Job assigned by vendor', timestamp: '2026-09-10T08:30:00Z', status: 'assigned' },
    ],
  },
  {
    job_id: 'WO-2026-00002',
    work_order_id: 'WO-2026-00002',
    complaint_id: 'CMP-2026-00005',
    title: 'Leaking Pipe — Block C Production Floor',
    description: 'Water leak detected near production line 3. Pipe joint failure suspected. Water pooling on the floor creating safety hazard.',
    location: 'Plant 1 / Block C / Floor 1 / Production Line 3',
    category: 'Plumbing',
    priority: 'critical',
    status: 'accepted',
    assigned_at: '2026-09-09T14:00:00Z',
    accepted_at: '2026-09-09T14:45:00Z',
    sla_deadline: '2026-09-09T18:00:00Z',
    technician_id: 'USR-00006',
    technician_name: 'Ahmad Khan',
    photos: [],
    materials: [],
    progress_updates: [
      { id: 'PU-002', message: 'Job assigned by vendor', timestamp: '2026-09-09T14:00:00Z', status: 'assigned' },
      { id: 'PU-003', message: 'Accepted job, en route to site', timestamp: '2026-09-09T14:45:00Z', status: 'accepted' },
    ],
  },
  {
    job_id: 'WO-2026-00003',
    work_order_id: 'WO-2026-00003',
    complaint_id: 'CMP-2026-00008',
    title: 'Elevator Stuck Between Floors — Building B',
    description: 'Main passenger elevator stuck between floor 2 and 3. Passengers were evacuated safely. Elevator requires immediate technical inspection.',
    location: 'Plant 1 / Building B / Elevator Shaft',
    category: 'Elevator / Lift',
    priority: 'critical',
    status: 'in_progress',
    assigned_at: '2026-09-09T10:00:00Z',
    accepted_at: '2026-09-09T10:15:00Z',
    started_at: '2026-09-09T10:30:00Z',
    sla_deadline: '2026-09-09T14:00:00Z',
    technician_id: 'USR-00006',
    technician_name: 'Ahmad Khan',
    photos: [
      { id: 'PH-001', stage: 'before', data_url: '', captured_at: '2026-09-09T10:35:00Z', notes: 'Elevator display shows error code E-12' },
    ],
    materials: [],
    progress_updates: [
      { id: 'PU-004', message: 'Job assigned by vendor', timestamp: '2026-09-09T10:00:00Z', status: 'assigned' },
      { id: 'PU-005', message: 'Accepted and en route', timestamp: '2026-09-09T10:15:00Z', status: 'accepted' },
      { id: 'PU-006', message: 'Started work — inspecting elevator motor', timestamp: '2026-09-09T10:30:00Z', status: 'in_progress' },
    ],
  },
  {
    job_id: 'WO-2026-00004',
    work_order_id: 'WO-2026-00004',
    complaint_id: 'CMP-2026-00012',
    title: 'Broken Ceiling Tile — Admin Office',
    description: 'Ceiling tile damaged in admin office room 204. Water stain visible above. Potential leak from floor above.',
    location: 'Plant 1 / Block A / Floor 2 / Room 204',
    category: 'Civil Works',
    priority: 'medium',
    status: 'pending_inspection',
    assigned_at: '2026-09-08T09:00:00Z',
    accepted_at: '2026-09-08T09:20:00Z',
    started_at: '2026-09-08T09:45:00Z',
    completed_at: '2026-09-08T12:30:00Z',
    sla_deadline: '2026-09-09T09:00:00Z',
    technician_id: 'USR-00006',
    technician_name: 'Ahmad Khan',
    photos: [
      { id: 'PH-002', stage: 'before', data_url: '', captured_at: '2026-09-08T09:50:00Z', notes: 'Damaged ceiling tile' },
      { id: 'PH-003', stage: 'after', data_url: '', captured_at: '2026-09-08T12:25:00Z', notes: 'Tile replaced and area cleaned' },
    ],
    materials: [
      { id: 'MT-001', part_name: 'Ceiling Tile 600x600mm', quantity: 2, unit: 'pcs', unit_cost: 45.00, total_cost: 90.00 },
      { id: 'MT-002', part_name: 'Sealant', quantity: 1, unit: 'tube', unit_cost: 12.50, total_cost: 12.50 },
    ],
    progress_updates: [
      { id: 'PU-007', message: 'Job assigned', timestamp: '2026-09-08T09:00:00Z', status: 'assigned' },
      { id: 'PU-008', message: 'Work started', timestamp: '2026-09-08T09:45:00Z', status: 'in_progress' },
      { id: 'PU-009', message: 'Work completed — tile replaced', timestamp: '2026-09-08T12:30:00Z', status: 'completed' },
    ],
    completion_notes: 'Replaced damaged ceiling tile. Checked for ongoing leaks — none found. Area clean.',
  },
];

function ok<T>(data: T): ApiResponse<T> {
  return { success: true, data, message: 'OK' };
}

function generateId(): string {
  return `PU-${Date.now()}`;
}

// ─── GAS API → TechnicianJob mapping ───────────────────────────────────────────

const STATUS_MAP: Record<string, JobStatus> = {
  assigned: 'assigned',
  Assigned: 'assigned',
  accepted: 'accepted',
  Accepted: 'accepted',
  in_progress: 'in_progress',
  'In Progress': 'in_progress',
  'Pending Parts': 'in_progress',
  pending_inspection: 'pending_inspection',
  Completed: 'pending_inspection',
  completed: 'completed',
  Inspected: 'completed',
  rejected: 'rejected',
  Rejected: 'rejected',
};

function normalizeStatus(value: unknown): JobStatus {
  return STATUS_MAP[String(value ?? '')] ?? 'assigned';
}

function normalizePriority(value: unknown): TechnicianJob['priority'] {
  const raw = String(value ?? '').toLowerCase();
  if (raw === 'low' || raw === 'medium' || raw === 'high' || raw === 'critical') return raw;
  return 'medium';
}

function mapApiJob(raw: any): TechnicianJob {
  const photos: JobPhoto[] = (raw?.photos ?? []).map((p: any) => ({
    id: p.id ?? p.photo_id ?? '',
    stage: p.stage,
    data_url: p.data_url ?? p.file_url ?? '',
    captured_at: p.captured_at ?? '',
    notes: p.notes ?? '',
  }));

  const materials: MaterialLog[] = (raw?.materials ?? []).map((m: any) => ({
    id: m.id ?? m.material_id ?? '',
    part_name: m.part_name ?? '',
    quantity: Number(m.quantity) || 0,
    unit: m.unit ?? 'pcs',
    unit_cost: Number(m.unit_cost) || 0,
    total_cost: Number(m.total_cost) || 0,
    notes: m.notes ?? '',
  }));

  const progress_updates: ProgressUpdate[] = (raw?.progress_updates ?? []).map((p: any) => ({
    id: p.id ?? p.progress_id ?? '',
    message: p.message ?? '',
    timestamp: p.timestamp ?? p.created_at ?? '',
    status: normalizeStatus(p.status),
  }));

  return {
    job_id: raw?.job_id ?? raw?.work_order_id ?? '',
    work_order_id: raw?.work_order_id ?? raw?.job_id ?? '',
    complaint_id: raw?.complaint_id ?? '',
    title: raw?.title ?? '',
    description: raw?.description ?? raw?.scope_of_work ?? '',
    location: raw?.location ?? '',
    category: raw?.category ?? raw?.work_category_id ?? '',
    priority: normalizePriority(raw?.priority),
    status: normalizeStatus(raw?.status),
    assigned_at: raw?.assigned_at ?? raw?.created_at ?? '',
    accepted_at: raw?.accepted_at || undefined,
    started_at: raw?.started_at || undefined,
    completed_at: raw?.completed_at || undefined,
    sla_deadline: raw?.sla_deadline ?? raw?.target_date ?? '',
    technician_id: raw?.technician_id ?? '',
    technician_name: raw?.technician_name ?? '',
    photos,
    materials,
    progress_updates,
    completion_notes: raw?.completion_notes || undefined,
    digital_signature: raw?.digital_signature || undefined,
  };
}

function applyFilter(jobs: TechnicianJob[], filter?: TechnicianJobFilter): TechnicianJob[] {
  let result = [...jobs];
  if (filter?.status) result = result.filter(j => j.status === filter.status);
  if (filter?.search) {
    const q = filter.search.toLowerCase();
    result = result.filter(j =>
      j.title.toLowerCase().includes(q) ||
      j.work_order_id.toLowerCase().includes(q) ||
      j.location.toLowerCase().includes(q) ||
      j.category.toLowerCase().includes(q)
    );
  }
  return result;
}

export class GASTEchnicianAdapter implements ITechnicianAdapter {
  private jobs: TechnicianJob[] = [...DEMO_JOBS];

  private get isLive(): boolean {
    return hasApiEndpoint();
  }

  private async mutate(
    jobId: string,
    action: string,
    body: unknown
  ): Promise<ApiResponse<TechnicianJob>> {
    const res = await ApiClient.post<unknown>(`/technician/jobs/${jobId}/${action}`, body ?? {});
    if (!res.success) return res as unknown as ApiResponse<TechnicianJob>;
    return this.getJobById(jobId);
  }

  async getJobs(technicianId: string, filter?: TechnicianJobFilter): Promise<ApiResponse<TechnicianJob[]>> {
    if (this.isLive) {
      const params = new URLSearchParams();
      params.set('technician_id', technicianId);
      if (filter?.status) params.set('status', filter.status);
      if (filter?.search) params.set('search', filter.search);
      const res = await ApiClient.get<any[]>(`/technician/jobs?${params.toString()}`);
      if (!res.success) return res as unknown as ApiResponse<TechnicianJob[]>;
      return { ...res, data: (res.data ?? []).map(mapApiJob) };
    }

    await new Promise(r => setTimeout(r, 300));
    const mine = this.jobs.filter(j => j.technician_id === technicianId);
    return ok(applyFilter(mine, filter));
  }

  async getJobById(jobId: string): Promise<ApiResponse<TechnicianJob>> {
    if (this.isLive) {
      const res = await ApiClient.get<any>(`/technician/jobs/${jobId}`);
      if (!res.success) return res as unknown as ApiResponse<TechnicianJob>;
      return { ...res, data: mapApiJob(res.data) };
    }

    await new Promise(r => setTimeout(r, 200));
    const job = this.jobs.find(j => j.job_id === jobId);
    if (!job) return { success: false, data: null as unknown as TechnicianJob, message: 'Job not found', errors: ['Job not found'] };
    return ok(job);
  }

  async acceptJob(dto: AcceptJobDto): Promise<ApiResponse<TechnicianJob>> {
    if (this.isLive) return this.mutate(dto.job_id, 'accept', dto);

    await new Promise(r => setTimeout(r, 400));
    const idx = this.jobs.findIndex(j => j.job_id === dto.job_id);
    if (idx === -1) return { success: false, data: null as unknown as TechnicianJob, message: 'Job not found', errors: ['Job not found'] };
    const job = this.jobs[idx];
    this.jobs[idx] = {
      ...job,
      status: 'accepted' as JobStatus,
      accepted_at: dto.accepted_at,
      progress_updates: [
        ...job.progress_updates,
        { id: generateId(), message: 'Job accepted', timestamp: dto.accepted_at, status: 'accepted' },
      ],
    };
    return ok(this.jobs[idx]);
  }

  async startJob(dto: StartJobDto): Promise<ApiResponse<TechnicianJob>> {
    if (this.isLive) return this.mutate(dto.job_id, 'start', dto);

    await new Promise(r => setTimeout(r, 400));
    const idx = this.jobs.findIndex(j => j.job_id === dto.job_id);
    if (idx === -1) return { success: false, data: null as unknown as TechnicianJob, message: 'Job not found', errors: ['Job not found'] };
    const job = this.jobs[idx];
    this.jobs[idx] = {
      ...job,
      status: 'in_progress' as JobStatus,
      started_at: dto.started_at,
      progress_updates: [
        ...job.progress_updates,
        { id: generateId(), message: dto.notes || 'Work started', timestamp: dto.started_at, status: 'in_progress' },
      ],
    };
    return ok(this.jobs[idx]);
  }

  async addPhoto(dto: AddPhotoDto): Promise<ApiResponse<TechnicianJob>> {
    if (this.isLive) {
      return this.mutate(dto.job_id, 'photo', {
        stage: dto.photo.stage,
        data_url: dto.photo.data_url,
        notes: dto.photo.notes,
      });
    }

    await new Promise(r => setTimeout(r, 300));
    const idx = this.jobs.findIndex(j => j.job_id === dto.job_id);
    if (idx === -1) return { success: false, data: null as unknown as TechnicianJob, message: 'Job not found', errors: ['Job not found'] };
    const photo: JobPhoto = { ...dto.photo, id: `PH-${Date.now()}` };
    this.jobs[idx] = {
      ...this.jobs[idx],
      photos: [...this.jobs[idx].photos, photo],
    };
    return ok(this.jobs[idx]);
  }

  async addMaterial(dto: AddMaterialDto): Promise<ApiResponse<TechnicianJob>> {
    if (this.isLive) return this.mutate(dto.job_id, 'material', dto.material);

    await new Promise(r => setTimeout(r, 300));
    const idx = this.jobs.findIndex(j => j.job_id === dto.job_id);
    if (idx === -1) return { success: false, data: null as unknown as TechnicianJob, message: 'Job not found', errors: ['Job not found'] };
    const mat: MaterialLog = {
      ...dto.material,
      id: `MT-${Date.now()}`,
      total_cost: dto.material.quantity * dto.material.unit_cost,
    };
    this.jobs[idx] = {
      ...this.jobs[idx],
      materials: [...this.jobs[idx].materials, mat],
    };
    return ok(this.jobs[idx]);
  }

  async addProgress(dto: AddProgressDto): Promise<ApiResponse<TechnicianJob>> {
    if (this.isLive) {
      return this.mutate(dto.job_id, 'progress', { message: dto.message, status: dto.status });
    }

    await new Promise(r => setTimeout(r, 300));
    const idx = this.jobs.findIndex(j => j.job_id === dto.job_id);
    if (idx === -1) return { success: false, data: null as unknown as TechnicianJob, message: 'Job not found', errors: ['Job not found'] };
    const update: ProgressUpdate = {
      id: generateId(),
      message: dto.message,
      timestamp: new Date().toISOString(),
      status: dto.status,
    };
    this.jobs[idx] = {
      ...this.jobs[idx],
      progress_updates: [...this.jobs[idx].progress_updates, update],
    };
    return ok(this.jobs[idx]);
  }

  async completeJob(dto: CompleteJobDto): Promise<ApiResponse<TechnicianJob>> {
    if (this.isLive) return this.mutate(dto.job_id, 'complete', dto);

    await new Promise(r => setTimeout(r, 500));
    const idx = this.jobs.findIndex(j => j.job_id === dto.job_id);
    if (idx === -1) return { success: false, data: null as unknown as TechnicianJob, message: 'Job not found', errors: ['Job not found'] };
    const job = this.jobs[idx];
    this.jobs[idx] = {
      ...job,
      status: 'completed' as JobStatus,
      completed_at: dto.completed_at,
      completion_notes: dto.completion_notes,
      digital_signature: dto.digital_signature,
      progress_updates: [
        ...job.progress_updates,
        { id: generateId(), message: 'Work completed', timestamp: dto.completed_at, status: 'completed' },
      ],
    };
    return ok(this.jobs[idx]);
  }
}
